import { computed, unref } from "vue";
import i18n from "@/shared/config/i18n";
import { EnforcementInscriptionStatus } from "@/shared/config/enforcementInscriptionStatuses";
import {
  ParticipantRole,
  DEBTOR_ROLES,
} from "@/shared/config/participantRoles";

const t = (key) => i18n.global.t(key);

const DEFAULT_NOTARY_IDENTIFIER = "000000000000";

const STEP_KEYS = Object.freeze({
  CREATED: "created",
  PARTICIPANTS: "participants",
  EDITOR: "editor",
  ATTACHMENTS: "attachments",
  NOTARY: "notary",
  SIGN: "sign",
});

function hasParticipantRole(list, role) {
  return list.some((p) => p?.participant_type_code === role);
}

function hasDebtorRole(list) {
  return list.some((p) => DEBTOR_ROLES.includes(p?.participant_type_code));
}

function emptyMentionStats() {
  return { total: 0, withValue: 0, withoutValue: 0 };
}

function mergeMentionStats(a, b) {
  return {
    total: a.total + b.total,
    withValue: a.withValue + b.withValue,
    withoutValue: a.withoutValue + b.withoutValue,
  };
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function isNotaryMention(attrs) {
  if (!attrs) return false;
  if (attrs.isNotary === true) return true;
  const code = attrs.participant_type_code;
  if (typeof code === "string" && code.toUpperCase() === "NOTARY") return true;
  const display = attrs.displayKey;
  if (typeof display === "string" && /notary/i.test(display)) return true;
  return false;
}

function countParticipantMentions(documentJson) {
  if (!documentJson || !documentJson.content) return emptyMentionStats();
  let total = 0;
  let withValue = 0;
  let withoutValue = 0;
  function traverse(node) {
    if (!node || typeof node !== "object") return;
    if (node.type === "mention" && node.attrs && !isNotaryMention(node.attrs)) {
      total++;
      const hasValue =
        node.attrs.value && String(node.attrs.value).trim() !== "";
      if (hasValue) withValue++;
      else withoutValue++;
    }
    if (Array.isArray(node.content)) {
      node.content.forEach((child) => traverse(child));
    }
  }
  traverse(documentJson);
  return { total, withValue, withoutValue };
}

function collectMentionStats(form) {
  const sources = [form?.content, form?.content_kz, form?.content_en]
    .map(parseMaybeJson)
    .filter(Boolean);
  if (!sources.length) return emptyMentionStats();
  return sources.reduce(
    (acc, doc) => mergeMentionStats(acc, countParticipantMentions(doc)),
    emptyMentionStats()
  );
}

function isAssigned(form) {
  const id = form?.notary_identifier;
  return Boolean(id) && id !== DEFAULT_NOTARY_IDENTIFIER;
}

function isSigned(status) {
  return (
    status &&
    status !== EnforcementInscriptionStatus.PROJECT &&
    status !== EnforcementInscriptionStatus.PENDING_REVIEW
  );
}

export function useStepperState({
  form,
  participants,
  documents,
  activeTab,
  changeTab,
}) {
  const formRef = () => unref(form) || {};
  const participantsRef = () => unref(participants) || [];
  const documentsRef = () => unref(documents) || [];

  const status = computed(() => formRef().status);
  const isProject = computed(
    () => !status.value || status.value === EnforcementInscriptionStatus.PROJECT
  );

  const doneCreated = computed(() => Boolean(formRef().uuid || formRef().id));

  const doneParticipants = computed(() => {
    const list = participantsRef();
    return (
      hasParticipantRole(list, ParticipantRole.RECOVERER) && hasDebtorRole(list)
    );
  });

  const mentionStats = computed(() => {
    if (!doneParticipants.value) return emptyMentionStats();
    return collectMentionStats(formRef());
  });

  const doneEditor = computed(() => {
    if (!doneParticipants.value) return false;
    const s = mentionStats.value;
    if (s.total === 0) return true;
    return s.withoutValue === 0;
  });

  const doneAttachments = computed(() => documentsRef().length > 0);

  const doneNotary = computed(() => isAssigned(formRef()));

  const doneSign = computed(() => isSigned(status.value));

  const steps = computed(() => {
    const list = participantsRef();
    const recoverer = list.find(
      (p) => p?.participant_type_code === ParticipantRole.RECOVERER
    );
    const debtors = list.filter((p) =>
      DEBTOR_ROLES.includes(p?.participant_type_code)
    );
    const attachmentsCount = documentsRef().length;
    const notaryInfo = formRef().notary_info;
    const isP2 = formRef().exec_inscription_type === "P2";
    const mStats = mentionStats.value;

    const stepDefs = [
      {
        key: STEP_KEYS.CREATED,
        index: 1,
        title: t("Заявление создано"),
        description: t(
          "Заявление зарегистрировано в системе. Теперь заполните остальные шаги."
        ),
        icon: "huge-add-pen",
        done: doneCreated.value,
        summary: doneCreated.value ? formatCreatedSummary(formRef()) : "",
        targetTab: null,
        details: {
          todo: [
            t("Заявление зарегистрировано в системе с уникальным номером"),
            t("Зафиксированы дата и время создания"),
            t("Применён шаблон по выбранному виду исполнительной надписи"),
          ],
          doneData: doneCreated.value ? buildCreatedData(formRef()) : [],
          warning: null,
          actionLabel: null,
        },
      },
      {
        key: STEP_KEYS.PARTICIPANTS,
        index: 2,
        title: t("Добавление участников"),
        description: t(
          "Укажите взыскателя и должника. Данные подтянутся из ГБД ФЛ/ЮЛ автоматически."
        ),
        icon: "huge-users",
        done: doneParticipants.value,
        summary: doneParticipants.value
          ? formatParticipantsSummary(recoverer, debtors)
          : "",
        targetTab: "redactor",
        details: {
          todo: [
            t(
              "Введите ИИН взыскателя — данные подтянутся из ГБД ФЛ автоматически"
            ),
            t("Введите ИИН/БИН должника — можно добавить нескольких должников"),
            t(
              "Если ФЛ действует от имени ЮЛ — отметьте флаг и введите БИН организации"
            ),
          ],
          doneData: doneParticipants.value
            ? buildParticipantsData(recoverer, debtors)
            : [],
          warning: null,
          actionLabel: t("Открыть редактор"),
        },
      },
      {
        key: STEP_KEYS.EDITOR,
        index: 3,
        title: t("Работа с текстом заявления"),
        description: t(
          "Сначала добавьте всех участников — их данные подставятся в текст автоматически. Затем проверьте суммы, даты и реквизиты."
        ),
        icon: "huge-edit",
        done: doneEditor.value,
        summary: doneEditor.value ? t("Текст заполнен") : "",
        targetTab: "redactor",
        details: {
          todo: buildEditorTodo(mStats),
          doneData: doneEditor.value ? buildEditorData(formRef(), mStats) : [],
          warning: t(
            "Если изменить участника после ручных правок, ваши правки в этих местах могут перезаписаться."
          ),
          actionLabel: t("Открыть редактор"),
        },
      },
      {
        key: STEP_KEYS.ATTACHMENTS,
        index: 4,
        title: t("Вложение документов"),
        description: t(
          "Прикрепите PDF до 5 МБ: договор, претензию и расчёт задолженности."
        ),
        icon: "huge-file-attach",
        done: doneAttachments.value,
        summary: doneAttachments.value ? t("Файлов: ") + attachmentsCount : "",
        targetTab: "attachments",
        details: {
          todo: [
            t("Прикрепите договор или сделку, на которой основано требование"),
            t("Прикрепите претензию и подтверждение её направления должнику"),
            t("Прикрепите расчёт задолженности (основной долг и пеня)"),
            t("Размер каждого PDF не более 5 МБ"),
          ],
          doneData: doneAttachments.value
            ? buildAttachmentsData(documentsRef())
            : [],
          warning: null,
          actionLabel: t("Открыть вложения"),
        },
      },
      {
        key: STEP_KEYS.NOTARY,
        index: 5,
        title: isP2 ? t("Назначение нотариуса") : t("Выбор нотариуса"),
        description: isP2
          ? t(
              "Для вида ИН P2 нотариус назначается автоматически по территории должника."
            )
          : t(
              "Выберите нотариуса по территории регистрации должника из списка онлайн-нотариусов."
            ),
        icon: "huge-user-star",
        done: doneNotary.value,
        summary: doneNotary.value ? formatNotarySummary(notaryInfo) : "",
        targetTab: "notary",
        details: {
          todo: isP2
            ? [
                t(
                  "Для вида ИН P2 действует метод «барабан» — авто-распределение"
                ),
                t(
                  "Нажмите «Назначить нотариуса» — система выберет онлайн-нотариуса"
                ),
                t("Нотариус определяется по территории регистрации должника"),
              ]
            : [
                t(
                  "В списке только онлайн-нотариусы с услугой «Исполнительная надпись»"
                ),
                t("Нотариус выбирается по территории регистрации должника"),
                t("Кликните по нотариусу в списке для назначения"),
              ],
          doneData: doneNotary.value ? buildNotaryData(notaryInfo) : [],
          warning: null,
          actionLabel: t("Открыть подписание"),
        },
      },
      {
        key: STEP_KEYS.SIGN,
        index: 6,
        title: t("Подписание заявления"),
        description: t(
          "После подписания ЭЦП заявление автоматически отправится нотариусу. Изменить данные после отправки нельзя."
        ),
        icon: "huge-list-check",
        done: doneSign.value,
        summary: doneSign.value ? t("Подписано и отправлено") : "",
        targetTab: "notary",
        details: {
          todo: [
            t("Нажмите «Подписать ЭЦП» в табе «Подписание»"),
            t("Выберите сертификат через NCALayer"),
            t("После подписи заявление автоматически уйдёт нотариусу"),
          ],
          doneData: doneSign.value ? buildSignData(formRef()) : [],
          warning: t(
            "После отправки заявления нотариусу изменить данные нельзя."
          ),
          actionLabel: t("Открыть подписание"),
        },
      },
    ];

    const firstTodoIndex = stepDefs.findIndex((s) => !s.done);
    const at = unref(activeTab);
    return stepDefs.map((step, i) => ({
      ...step,
      status: getStatus(step, i, firstTodoIndex),
      isCurrentTab: Boolean(step.targetTab) && step.targetTab === at,
    }));
  });

  const totalSteps = computed(() => steps.value.length);
  const doneCount = computed(
    () => steps.value.filter((s) => s.status === "done").length
  );
  const progress = computed(() =>
    totalSteps.value === 0
      ? 0
      : Math.round((doneCount.value / totalSteps.value) * 100)
  );

  // Если в документе нет mentions для заполнения — шаг «Работа с текстом»
  // пропускаем и открываем сразу следующую вкладку «Вложения».
  function resolveTargetTab(step) {
    if (
      step?.key === STEP_KEYS.EDITOR &&
      doneParticipants.value &&
      mentionStats.value.total === 0
    ) {
      return "attachments";
    }
    return step?.targetTab || null;
  }

  function goTo(step) {
    if (!step) return;
    const tab = resolveTargetTab(step);
    if (tab && tab !== unref(activeTab)) {
      changeTab(tab);
    }
  }

  return {
    steps,
    totalSteps,
    doneCount,
    progress,
    isProject,
    goTo,
    doneCreated,
    doneParticipants,
    doneEditor,
    doneAttachments,
    doneNotary,
    doneSign,
    mentionStats,
  };
}

function getStatus(step, i, firstTodoIndex) {
  if (step.done) return "done";
  if (firstTodoIndex === i) return "current";
  return "todo";
}

function formatCreatedSummary(form) {
  const number = form?.reg_number || form?.number || form?.id;
  const created = form?.created_at;
  const parts = [];
  if (number) parts.push("№ " + number);
  if (created) parts.push(formatDate(created));
  return parts.join(" • ");
}

function formatParticipantsSummary(recoverer, debtors) {
  const total = (recoverer ? 1 : 0) + (debtors ? debtors.length : 0);
  if (!total) return "";
  return t("Добавлено участников: ") + total;
}

function formatNotarySummary(info) {
  if (!info) return "";
  const fio = [info.last_name, info.first_name, info.middle_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fio || info.identifier || "";
}

function formatParticipantName(p) {
  if (!p) return "";
  const fio = [p.last_name, p.first_name, p.middle_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fio || p.full_name || p.identifier || "";
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return dd + "." + mm + "." + yy + ", " + hh + ":" + mi;
}

function buildCreatedData(form) {
  const rows = [];
  const number = form?.reg_number || form?.number || form?.id;
  if (number) rows.push({ label: t("Номер заявления"), value: "№ " + number });
  if (form?.created_at) {
    rows.push({ label: t("Создано"), value: formatDateTime(form.created_at) });
  }
  if (form?.exec_inscription_type) {
    rows.push({
      label: t("Вид ИН"),
      value: form.exec_inscription_type,
    });
  }
  return rows;
}

function buildParticipantsData(recoverer, debtors) {
  const rows = [];
  if (recoverer) {
    rows.push({
      label: t("Взыскатель"),
      value: formatParticipantName(recoverer),
    });
  }
  if (debtors && debtors.length) {
    debtors.forEach((d, i) => {
      const label =
        debtors.length > 1 ? t("Должник") + " " + (i + 1) : t("Должник");
      rows.push({ label, value: formatParticipantName(d) });
    });
  }
  return rows;
}

function buildEditorTodo(stats) {
  const items = [
    t("Проверьте ФИО, ИИН и адреса — они подставлены автоматически из ГБД"),
    t("Укажите сумму долга и государственную пошлину"),
    t("Укажите № и дату договора, проверьте расчёт пени"),
    t("Переключите язык ru/kk и проверьте обе версии текста"),
  ];
  if (stats && stats.total > 0 && stats.withoutValue > 0) {
    items.unshift(
      t("Незаполнено полей: ") +
        stats.withoutValue +
        t(" — заполните их в редакторе")
    );
  }
  return items;
}

function buildEditorData(form, stats) {
  const rows = [];
  if (stats && stats.total > 0) {
    rows.push({
      label: t("Заполнено полей"),
      value: stats.withValue + "/" + stats.total,
    });
  } else {
    rows.push({
      label: t("Статус"),
      value: t("Текст шаблона"),
    });
  }
  if (form?.contract_amount) {
    rows.push({
      label: t("Сумма долга"),
      value: String(form.contract_amount),
    });
  }
  if (form?.state_duty) {
    rows.push({
      label: t("Госпошлина"),
      value: String(form.state_duty),
    });
  }
  return rows;
}

function buildAttachmentsData(documents) {
  if (!documents || !documents.length) return [];
  return documents.map((doc, i) => ({
    label: t("Файл") + " " + (i + 1),
    value:
      doc?.original_name ||
      doc?.file_name ||
      doc?.name ||
      doc?.title ||
      t("документ"),
  }));
}

function buildNotaryData(info) {
  if (!info) return [];
  const rows = [];
  const fio = formatParticipantName(info);
  if (fio) rows.push({ label: t("Нотариус"), value: fio });
  const region = info.region || info.area || info.city;
  if (region) rows.push({ label: t("Регион"), value: region });
  const address = info.address || info.work_address;
  if (address) rows.push({ label: t("Адрес"), value: address });
  if (info.identifier) {
    rows.push({ label: t("ИИН"), value: info.identifier });
  }
  return rows;
}

function buildSignData(form) {
  const rows = [];
  rows.push({ label: t("Статус"), value: t("Подписано и отправлено") });
  if (form?.signed_at) {
    rows.push({
      label: t("Подписано"),
      value: formatDateTime(form.signed_at),
    });
  } else if (form?.updated_at) {
    rows.push({
      label: t("Обновлено"),
      value: formatDateTime(form.updated_at),
    });
  }
  return rows;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return dd + "." + mm + "." + yy;
}

export { STEP_KEYS };
